import { Button } from '@mui/material'
import { ICellRendererParams } from 'ag-grid-community'
import { createPartnerCode, getPartnerCodes, updatePartnerCode } from 'api/partnerCode'
import { useAuthContext } from 'components/context/AuthProvider'
import NotificationSys from 'components/NotificationSystem'
import InfinityAgGrid from 'components/table/InfinityAgGrid'
import TableToolbar from 'components/table/TableToolbar'
import { getActionColumn } from 'features/UI'
import { useExportCSV } from 'hooks/useExportCSV'
import { useGridControl } from 'hooks/useGridControl'
import { useShowControl } from 'hooks/useShowControl'
import { ReactNode, useCallback, useMemo, useState } from 'react'
import { useMutation } from 'react-query'
import { BaseTableRequest, FilterModel, Nullish, PartnerCode } from 'types'
import { useGetDataSource } from 'utils'
import { usePartnerCodeFilters } from '../../hooks/usePartnerCodeFilters'
import { PartnerCodeGenerationModal } from '../PartnerCodeGenerationModal/PartnerCodeGenerationModal'
import { csvPartnerCodeColumns, partnerCodeColumns } from './columnDefs'

interface Props {
  left: ReactNode
}

export const PartnerCodesTab = ({ left }: Props) => {
  const { isAdmin } = useAuthContext()

  const [isOpenModal, handleOpenModal, handleCloseModal] = useShowControl()
  const [editedItem, setEditedItem] = useState<Nullish<PartnerCode>>(null)

  const openGenerateNew = useCallback(() => {
    setEditedItem(null)
    handleOpenModal()
  }, [handleOpenModal])

  const handleClickEdit = useCallback(
    (_: number, item: ICellRendererParams<PartnerCode>) => {
      if (!item.data) return

      setEditedItem(item.data)
      handleOpenModal()
    },
    [handleOpenModal],
  )

  const columns = useMemo(() => {
    if (!isAdmin) {
      return partnerCodeColumns
    }

    return [
      ...partnerCodeColumns,
      getActionColumn([
        {
          name: 'Edit',
          onClick: handleClickEdit,
        },
      ]),
    ]
  }, [handleClickEdit, isAdmin])

  const { PartnerCodeFilters, codeTypeFilterValue, statusFilterValue } = usePartnerCodeFilters()

  // Code and organization are filtered from the column headers; type and status come from the
  // toolbar dropdowns and are merged into the same request.
  const getPartnerCodesWrapper = useCallback(
    (request: BaseTableRequest) => {
      const filter: FilterModel = { ...request.filter }

      if (codeTypeFilterValue) {
        filter.codeType = codeTypeFilterValue
      }

      if (statusFilterValue) {
        filter.status = statusFilterValue
      }

      return getPartnerCodes({ ...request, filter })
    },
    [codeTypeFilterValue, statusFilterValue],
  )

  const getDataSource = useGetDataSource(getPartnerCodesWrapper)
  const { gridApi, onGridReady, columnApi } = useGridControl(getDataSource)

  const onCloseModal = useCallback(() => {
    setEditedItem(null)
    handleCloseModal()
  }, [handleCloseModal])

  const createMutation = useMutation(createPartnerCode, {
    onSuccess: (data) => {
      onCloseModal()
      gridApi?.purgeInfiniteCache()
      NotificationSys.showSuccess(`Created partner code ${data.data.code}`)
    },
  })

  const updateMutation = useMutation(updatePartnerCode, {
    onSuccess: (data) => {
      onCloseModal()
      gridApi?.purgeInfiniteCache()
      NotificationSys.showSuccess(`Updated partner code ${data.data.code}`)
    },
  })

  const { csvLoading, onExportCSV } = useExportCSV(
    getPartnerCodesWrapper,
    gridApi,
    columnApi,
    csvPartnerCodeColumns,
    'Partner Codes.csv',
  )

  return (
    <>
      <TableToolbar
        exportCSVEnable={true}
        left={left}
        onExportCSV={onExportCSV}
        csvLoading={csvLoading}
      >
        {isAdmin ? (
          <Button variant="outlined" sx={{ minWidth: 120 }} onClick={openGenerateNew}>
            Generate Code
          </Button>
        ) : null}
      </TableToolbar>

      {PartnerCodeFilters}

      <InfinityAgGrid pagination columnDefs={columns} onGridReady={onGridReady} />

      <PartnerCodeGenerationModal
        isOpen={isOpenModal}
        handleClose={onCloseModal}
        onGenerate={createMutation.mutate}
        onUpdate={updateMutation.mutate}
        loading={createMutation.isLoading || updateMutation.isLoading}
        editedItem={editedItem}
      />
    </>
  )
}
